/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { isSegmentable } from './typeGuards/isSegmentable';
import { matchesSegment } from './matchesSegment';

export const deepSegmentationFilter = <T>(item: T | undefined, userSegments?: string[]) => {
  if (item === undefined) {
    return undefined;
  }

  if (Array.isArray(item)) {
    const filteredArray = item.reduce<T[]>((acc, entry) => {
      if (isSegmentable(entry)) {
        const isMatchingUserSegments = matchesSegment(userSegments, entry.segmentation.segment);
        if (!isMatchingUserSegments) {
          return acc;
        }
      }

      const updatedEntry = deepSegmentationFilter(entry as unknown, userSegments);

      if (updatedEntry !== undefined) {
        acc.push(updatedEntry as T);
      }

      return acc;
    }, []);

    return filteredArray as T;
  }

  if (typeof item === 'object' && item !== null) {
    if (isSegmentable(item)) {
      if (!matchesSegment(userSegments, item.segmentation.segment)) {
        return undefined;
      }
    }

    const filteredObject = Object.entries(item).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        if (key !== 'segmentation' && key !== 'segment') {
          const nestedValue = deepSegmentationFilter(value as unknown, userSegments);

          if (nestedValue !== undefined) {
            acc[key] = nestedValue;
          }
        } else {
          acc[key] = value;
        }

        return acc;
      },
      {},
    );

    return filteredObject as T;
  }

  return item;
};
